import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip } from 'react-leaflet'
import type { LatLngBoundsExpression, LatLngExpression } from 'leaflet'
import type { Trackpoint } from '../api/types'

/**
 * The recorded route on an OpenStreetMap background.
 *
 * OSM tiles rather than a commercial basemap, so the site needs no API key and no billing account
 * to show a map. Attribution is required by the tile usage policy and is rendered by Leaflet.
 */
export function RouteMap({ points }: { points: Trackpoint[] }) {
  const located = points.filter(
    (p): p is Trackpoint & { lat: number; lon: number } => p.lat !== undefined && p.lon !== undefined,
  )

  if (located.length < 2) {
    return (
      <div className="map-empty">
        <div>
          <strong>No route to draw.</strong>
          <p style={{ marginBottom: 0 }}>
            This activity has no GPS positions — recorded indoors, or the fix never arrived. Heart rate
            and pace below are still from the file.
          </p>
        </div>
      </div>
    )
  }

  const line: LatLngExpression[] = located.map((p) => [p.lat, p.lon])
  const lats = located.map((p) => p.lat)
  const lons = located.map((p) => p.lon)
  const bounds: LatLngBoundsExpression = [
    [Math.min(...lats), Math.min(...lons)],
    [Math.max(...lats), Math.max(...lons)],
  ]

  const start = line[0]
  const finish = line[line.length - 1]

  return (
    <MapContainer bounds={bounds} boundsOptions={{ padding: [24, 24] }} className="map" scrollWheelZoom>
      <TileLayer
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        maxZoom={19}
      />
      <Polyline positions={line} pathOptions={{ color: '#16a34a', weight: 4, opacity: 0.9 }} />
      {start && (
        <CircleMarker center={start} radius={7} pathOptions={{ color: '#16a34a', fillColor: '#16a34a', fillOpacity: 1 }}>
          <Tooltip>Start</Tooltip>
        </CircleMarker>
      )}
      {finish && (
        <CircleMarker center={finish} radius={7} pathOptions={{ color: '#b91c1c', fillColor: '#b91c1c', fillOpacity: 1 }}>
          <Tooltip>Finish</Tooltip>
        </CircleMarker>
      )}
    </MapContainer>
  )
}
